import { notification, Spin } from "antd";
import {
  countBy,
  fromPairs,
  get,
  isEmpty,
  isEqual,
  isNil,
  uniqBy,
} from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Button from "shared/components/Button";
import { PayloadPathsProvider } from "shared/components/PayloadPaths";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { DOCUMENT_TEMPLATE_STATES, SCREENS } from "utils/constants";
import { documentState } from "utils/helpers/documentTemplates";
import { fieldPhrase } from "../../data-fields";
import {
  isZoneSettled,
  readiness,
  unsettledZones,
  ZONE_TYPES,
  zonesOf,
} from "../../field-status";
import {
  DEFAULT_SAMPLE_PAYLOAD_KEY,
  findSamplePayload,
} from "../../sample-payloads";
import AllFields from "./all-fields";
import { draftState } from "./draft-content";
import EditorHeader from "./editor-header";
import { editorMenuItems, MENU_KEYS, publishStateOf } from "./editor-menu";
import FieldPanel from "./field-panel";
import HistorySheet from "./history-sheet";
import { pathOptions } from "./payload-paths";
import { saveBytes } from "./pdf-reader";
import PreviewPane from "./preview-pane";
import PublishSheet from "./publish-sheet";
import RenameModal from "./rename-modal";
import ReplacePdf from "./replace-pdf";
import SettleList, { EditorIntro } from "./settle-list";
import TestDataPicker, { TEST_DATA_KINDS } from "./test-data-picker";
import UsageLine, { NEW_USE } from "./usage-line";
import useLucieProposals from "./use-lucie-proposals";
import configTransfer from "./config-transfer";
import useDraftCommands from "./use-draft-commands";
import useEditorIntro from "./use-editor-intro";
import useLeaving from "./use-leaving";
import usePreview from "./use-preview";
import useSideFocus from "./use-side-focus";
import useTemplateDraft from "./use-template-draft";
import useZoneSource from "./use-zone-source";
import { zoneBoxes } from "./zone-markers";
import { listGroups, pdfSections, SECTION_MODES } from "./zone-sections";
import {
  acceptProposal,
  boxRowIndex,
  commitZone,
  leaveZoneEmpty,
  pendingProposalOf,
  proposalValueText,
  restoreZones,
  SUMMARY_KINDS,
  undoProposal,
  ZONE_STATUSES,
  zoneField,
  zoneLabel,
  zoneMappingKeys,
  zoneSnapshot,
  zoneStatus,
  zoneSummary,
  zoneTip,
} from "./zones";

const VIEWS = { SETTLE: "settle", ALL: "all" };
const SHEETS = { PUBLISH: "publish", HISTORY: "history" };
const MODALS = { REPLACE_PDF: "replacePdf", RENAME: "rename" };
const HOVER_SCROLL_DELAY = 400;
const UNDO_KEY = "dynamic-documents-undo";
const UNDO_DURATION = 6;
const NO_PLACES = new Map();

const sampleSelection = (key) => {
  const sample = findSamplePayload(key);
  return {
    kind: TEST_DATA_KINDS.SAMPLE,
    key: sample.key,
    label: sample.label,
    payload: sample.payload,
    paths: [],
  };
};

const focusOn = (zoneId) => ({ zoneId, nonce: Date.now() });

const Edit = () => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: templateData,
    loading: templateLoading,
    refetch: refetchTemplate,
  } = useQuery(graphql.queries.DOCUMENT_TEMPLATE, {
    variables: { where: { id } },
    fetchPolicy: "network-only",
  });
  const {
    data: draftData,
    loading: draftLoading,
    refetch: refetchDraft,
  } = useQuery(graphql.queries.DOCUMENT_TEMPLATE_DRAFT, {
    variables: { where: { id } },
    fetchPolicy: "network-only",
  });
  const { data: plansData } = useQuery(graphql.queries.INSURANCE_PLAN_CHOICES, {
    variables: { orderBy: [{ name: "ASC" }] },
  });
  const template = get(templateData, "documentTemplate");
  const [view, setView] = useState(VIEWS.SETTLE);
  const [panel, setPanel] = useState(null);
  const [testData, setTestData] = useState(() =>
    sampleSelection(DEFAULT_SAMPLE_PAYLOAD_KEY),
  );
  const [testedWithRealProject, setTestedWithRealProject] = useState(false);
  const [realOpen, setRealOpen] = useState(false);
  const [usesOpen, setUsesOpen] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [modal, setModal] = useState(null);
  const [listFocus, setListFocus] = useState(null);
  const [previewFocus, setPreviewFocus] = useState(null);
  const [pointerZoneId, setPointerZoneId] = useState(null);
  const [groupHoverIds, setGroupHoverIds] = useState(null);
  const [focusZoneId, setFocusZoneId] = useState(null);
  const [focusRequest, setFocusRequest] = useState(null);
  const intro = useEditorIntro();
  const { leaving, startLeaving } = useLeaving();
  const initializedRef = useRef(false);
  const hoverTimerRef = useRef(null);
  const importRef = useRef(null);
  const sideRef = useRef(null);

  const draft = useTemplateDraft({
    template: { id },
    onConflict: () => reloadAfterConflict(),
  });
  const { content, pendingFile, hasDraft, dirty, reset, update } = draft;
  const lucie = useLucieProposals({ template: { id }, content, update, t });

  const reload = async () => {
    const [templateResult, draftResult] = await Promise.all([
      refetchTemplate(),
      refetchDraft(),
    ]);
    const fresh = get(templateResult, "data.documentTemplate");
    if (isNil(fresh)) return false;
    reset(
      draftState({
        draft: get(draftResult, "data.documentTemplateDraft"),
        template: fresh,
      }),
    );
    setPanel(null);
    return true;
  };

  const reloadAfterConflict = async () => {
    const reloaded = await reload().catch(() => false);
    notification.warning({
      message: t(reloaded ? "HEADER.CONFLICT" : "HEADER.RELOAD_FAILED"),
    });
    return reloaded;
  };

  useEffect(() => {
    if (
      initializedRef.current ||
      templateLoading ||
      draftLoading ||
      isNil(template)
    )
      return;
    initializedRef.current = true;
    reset(
      draftState({
        draft: get(draftData, "documentTemplateDraft"),
        template,
      }),
    );
  }, [template, templateLoading, draftLoading, draftData, reset]);

  const zones = useMemo(
    () => (isNil(content) ? [] : zonesOf(content.anchors)),
    [content],
  );
  const unsettled = useMemo(
    () =>
      isNil(content)
        ? []
        : unsettledZones(content.anchors, content.anchorMappings),
    [content],
  );
  const { ready, total } = isNil(content)
    ? { ready: 0, total: 0 }
    : readiness(content.anchors, content.anchorMappings);
  const panelZone = isNil(panel)
    ? null
    : (zones.find((zone) => zone.id === panel.zoneId) ?? null);
  const workingMappings = useMemo(
    () =>
      isNil(panelZone)
        ? content?.anchorMappings
        : commitZone(content, panelZone, panel.mappings).anchorMappings,
    [content, panelZone, panel],
  );

  const preview = usePreview({
    template: { id },
    mappings: workingMappings,
    payload: testData.payload,
    enabled: !isNil(content) && isNil(pendingFile),
  });
  const zoneSource = useZoneSource({
    template: { id },
    version: isNil(content)
      ? null
      : (get(pendingFile, "key") ??
        `live:${get(template, "s3Key") ?? ""}:${get(template, "publishedAt") ?? ""}`),
    pending: !isNil(pendingFile),
  });
  const boxes = useMemo(
    () =>
      isNil(zoneSource.source)
        ? {}
        : zoneBoxes(zoneSource.source.markers, zones),
    [zoneSource.source, zones],
  );
  const unresolved = useMemo(
    () => new Set(isNil(pendingFile) ? preview.unresolved : []),
    [pendingFile, preview.unresolved],
  );
  const unsettledIds = useMemo(
    () => new Set(unsettled.map((zone) => zone.id)),
    [unsettled],
  );
  const statusOf = useCallback(
    (zone) =>
      zoneStatus({
        zone,
        mappings: get(content, "anchorMappings") || {},
        proposals: get(content, "proposals") || {},
        unresolved,
      }),
    [content, unresolved],
  );
  const zoneById = useMemo(
    () => new Map(zones.map((zone) => [zone.id, zone])),
    [zones],
  );
  const sections = useMemo(() => {
    if (zoneSource.loading) return { mode: SECTION_MODES.LOADING };
    const located = isNil(zoneSource.source)
      ? null
      : pdfSections({
          zones,
          boxes,
          headings: zoneSource.source.headings ?? [],
          pages: zoneSource.source.pages ?? [],
          labelOf: (zone) => zoneLabel(zone, t),
        });
    return isNil(located)
      ? { mode: SECTION_MODES.DATA }
      : { mode: SECTION_MODES.PDF, ...located };
  }, [zoneSource.loading, zoneSource.source, zones, boxes, t]);
  const groups = useMemo(
    () =>
      listGroups({
        sections,
        zones,
        mappings: get(content, "anchorMappings") || {},
        t,
      }),
    [sections, zones, content, t],
  );
  const zoneInfo = useMemo(() => {
    const mappings = get(content, "anchorMappings") || {};
    const proposals = get(content, "proposals") || {};
    const titles = new Map();
    for (const group of groups) {
      const short = group.zoneIds
        .map((zoneId) => zoneById.get(zoneId))
        .filter((zone) => !isNil(zone))
        .map((zone) => ({
          zone,
          title: zoneLabel(zone, t, { section: group.context }),
        }));
      const counts = countBy(short, "title");
      for (const { zone, title } of short)
        titles.set(zone.id, counts[title] > 1 ? zoneLabel(zone, t) : title);
    }
    const infos = new Map(
      zones.map((zone) => {
        const status = statusOf(zone);
        const field =
          pendingProposalOf(zone, proposals)?.field ??
          zoneField(zone, mappings);
        return [
          zone.id,
          {
            label: zoneLabel(zone, t),
            title: titles.get(zone.id) ?? zoneLabel(zone, t),
            status,
            phrase: isNil(field) ? null : fieldPhrase(field),
            tip: zoneTip({ zone, status, mappings, proposals, t }),
          },
        ];
      }),
    );
    return (zoneId) => infos.get(zoneId);
  }, [zones, zoneById, groups, content, statusOf, t]);
  const titleOf = useCallback(
    (zone) => zoneInfo(zone.id)?.title ?? zoneLabel(zone, t),
    [zoneInfo, t],
  );
  const paths = useMemo(
    () =>
      pathOptions({ payload: testData.payload, schemaPaths: testData.paths }),
    [testData],
  );
  const plans = useMemo(
    () =>
      uniqBy(
        [
          ...(get(plansData, "insurancePlans.data") || []).map(
            ({ id: value, name }) => ({ value, label: name }),
          ),
          ...(get(template, "usages") || []).map(
            ({ insurancePlanId, insurancePlanName }) => ({
              value: insurancePlanId,
              label: insurancePlanName ?? insurancePlanId,
            }),
          ),
        ],
        "value",
      ),
    [plansData, template],
  );
  const planNames = useMemo(
    () => fromPairs(plans.map(({ value, label }) => [value, label])),
    [plans],
  );

  useSideFocus({ sideRef, listFocus, focusRequest });

  useEffect(() => {
    setGroupHoverIds(null);
  }, [content, lucie.running]);

  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  const panelEdited = () =>
    !isNil(panelZone) &&
    zoneMappingKeys(panelZone).some(
      (key) => !isEqual(panel.mappings[key], content.anchorMappings[key]),
    );

  const showZone = (zone) => {
    if (panel?.zoneId === zone.id) return;
    const saved = saveOpenPanel();
    setPanel({ zoneId: zone.id, mappings: saved.anchorMappings });
  };

  const openZone = (zone) => {
    showZone(zone);
    setPreviewFocus(focusOn(zone.id));
  };

  const toggleZone = (zone) => {
    if (panel?.zoneId !== zone.id) {
      openZone(zone);
      return;
    }
    saveOpenPanel();
    setPanel(null);
  };

  const openFromPreview = (zoneId) => {
    const zone = zones.find((candidate) => candidate.id === zoneId);
    if (isNil(zone)) return;
    if (!unsettledIds.has(zone.id)) setView(VIEWS.ALL);
    showZone(zone);
    setListFocus(focusOn(zone.id));
  };

  const hoverRow = (zone) => {
    clearTimeout(hoverTimerRef.current);
    setPointerZoneId(zone?.id ?? null);
    if (isNil(zone)) return;
    hoverTimerRef.current = setTimeout(
      () => setPreviewFocus(focusOn(zone.id)),
      HOVER_SCROLL_DELAY,
    );
  };

  const focusRow = (zone) => {
    setFocusZoneId(zone.id);
    setPreviewFocus(focusOn(zone.id));
  };

  const blurRow = (zone) =>
    setFocusZoneId((current) => (current === zone.id ? null : current));

  const showUndo = ({ snapshot, ids, message }) =>
    notification.open({
      key: UNDO_KEY,
      message,
      placement: "bottomLeft",
      duration: UNDO_DURATION,
      btn: (
        <button
          type="button"
          className="dynamic-documents-editor__link"
          onClick={() => {
            setPanel((current) =>
              !isNil(current) && ids.includes(current.zoneId) ? null : current,
            );
            update((current) => restoreZones(current, snapshot));
            notification.destroy(UNDO_KEY);
          }}
        >
          {t("ZONES.UNDO")}
        </button>
      ),
    });

  const applyAction = ({ targets, change, message }) => {
    const snapshot = zoneSnapshot(content, targets);
    const next = change(content);
    const ids = targets.map((zone) => zone.id);
    update(change);
    intro.dismiss();
    if (ids.includes(panel?.zoneId)) setPanel(null);
    if (view === VIEWS.SETTLE)
      startLeaving(
        Object.fromEntries(
          targets
            .filter(
              (zone) =>
                unsettledIds.has(zone.id) &&
                isZoneSettled(zone, next.anchorMappings),
            )
            .map((zone) => [
              zone.id,
              { status: statusOf(zone), proposals: content.proposals },
            ]),
        ),
      );
    showUndo({ snapshot, ids, message });
    return { next, ids };
  };

  const runAction = (action) => {
    const fromSettle = view === VIEWS.SETTLE && !isEmpty(unsettled);
    const { ids } = applyAction(action);
    clearTimeout(hoverTimerRef.current);
    setPointerZoneId(null);
    setFocusZoneId(null);
    setGroupHoverIds(null);
    setFocusRequest({ ids, fromSettle, nonce: Date.now() });
  };

  const filledMessage = (zone, field) =>
    t(
      zone.type === ZONE_TYPES.CHILD_LOOP
        ? "ZONES.TOAST_COLUMN_FILLED"
        : "ZONES.TOAST_FILLED",
      { label: zoneLabel(zone, t), field: fieldPhrase(field) },
    );

  const savedMessage = (zone, mappings) => {
    const label = zoneLabel(zone, t);
    const summary = zoneSummary(zone, mappings);
    if (summary.kind === SUMMARY_KINDS.EMPTY)
      return t("ZONES.TOAST_EMPTY", { label });
    if (summary.kind === SUMMARY_KINDS.FIXED)
      return t("ZONES.TOAST_FIXED", { label, text: summary.text });
    if (
      (summary.kind === SUMMARY_KINDS.FIELD ||
        summary.kind === SUMMARY_KINDS.TABLE) &&
      !isNil(summary.field)
    )
      return filledMessage(zone, summary.field);
    return t("ZONES.TOAST_SAVED", { label });
  };

  const saveOpenPanel = () => {
    if (!panelEdited()) return content;
    const zone = panelZone;
    const { mappings } = panel;
    return applyAction({
      targets: [zone],
      change: (current) => commitZone(current, zone, mappings),
      message: savedMessage(zone, mappings),
    }).next;
  };

  const fillZone = (zone) => {
    const proposal = pendingProposalOf(zone, content.proposals);
    if (isNil(proposal)) return;
    runAction({
      targets: [zone],
      change: (current) => acceptProposal(current, zone),
      message: filledMessage(zone, proposal.field),
    });
  };

  const leaveEmpty = (zone) =>
    runAction({
      targets: [zone],
      change: (current) => leaveZoneEmpty(current, zone),
      message: t("ZONES.TOAST_EMPTY", { label: zoneLabel(zone, t) }),
    });

  const fillGroup = (targets) =>
    runAction({
      targets,
      change: (current) => targets.reduce(acceptProposal, current),
      message: t("ZONES.TOAST_GROUP_FILLED", { count: targets.length }),
    });

  const leaveGroup = (targets) =>
    runAction({
      targets,
      change: (current) => targets.reduce(leaveZoneEmpty, current),
      message: t("ZONES.TOAST_GROUP_EMPTY", { count: targets.length }),
    });

  const confirmPanel = () => {
    const zone = panelZone;
    const { mappings } = panel;
    setPanel(null);
    runAction({
      targets: [zone],
      change: (current) => commitZone(current, zone, mappings),
      message: savedMessage(zone, mappings),
    });
  };

  const ghostOf = (zoneId, box) => {
    const zone = zoneById.get(zoneId);
    if (
      isNil(zone) ||
      zone.id === panelZone?.id ||
      statusOf(zone) !== ZONE_STATUSES.CHECK
    )
      return null;
    return (
      proposalValueText({
        zone,
        proposal: pendingProposalOf(zone, content.proposals),
        payload: testData.payload,
        t,
        index:
          zone.type === ZONE_TYPES.CHILD_LOOP ? boxRowIndex(box) : undefined,
      }) ?? t("PREVIEW.GHOST_EMPTY")
    );
  };

  const {
    publishing,
    publish,
    discard,
    restore,
    applyNewFile,
    downloadOriginal,
  } = useDraftCommands({
    template: { id },
    live: template,
    draft,
    zoneSource,
    reload,
    reloadAfterConflict,
    closePanel: () => setPanel(null),
    closeSheet: () => setSheet(null),
    t,
  });

  const { exportConfig, importConfig } = configTransfer({
    content,
    update,
    onImported: () => setPanel(null),
    t,
  });

  const chooseProduct = () => {
    setSheet(null);
    if (isEmpty(content.uses))
      update((current) => ({ ...current, uses: [NEW_USE] }));
    setUsesOpen(true);
  };

  const MENU_ACTIONS = {
    [MENU_KEYS.REPLACE_PDF]: () => setModal(MODALS.REPLACE_PDF),
    [MENU_KEYS.RENAME]: () => setModal(MODALS.RENAME),
    [MENU_KEYS.DOWNLOAD_ORIGINAL]: downloadOriginal,
    [MENU_KEYS.DOWNLOAD_PREVIEW]: () =>
      saveBytes({
        bytes: preview.bytes,
        fileName: t("MENU.PREVIEW_FILE", {
          name: content.name || t("HEADER.UNTITLED"),
        }),
      }),
    [MENU_KEYS.HISTORY]: () => setSheet(SHEETS.HISTORY),
    [MENU_KEYS.DISCARD]: discard,
    [MENU_KEYS.EXPORT]: exportConfig,
    [MENU_KEYS.IMPORT]: () => importRef.current?.click(),
  };

  if (isNil(template))
    return (
      <div className="dynamic-documents-editor dynamic-documents-editor--centered">
        {templateLoading ? (
          <Spin />
        ) : (
          <>
            <p>{t("PAGE.NOT_FOUND")}</p>
            <Button
              type="primary--outlined"
              onClick={() =>
                navigate(SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.LIST.path)
              }
            >
              {t("PAGE.BACK_TO_LIST")}
            </Button>
          </>
        )}
      </div>
    );

  if (isNil(content))
    return (
      <div className="dynamic-documents-editor dynamic-documents-editor--centered">
        <Spin />
      </div>
    );

  const publishState = publishStateOf({
    ready,
    total,
    unpublished:
      hasDraft ||
      dirty ||
      documentState(template) === DOCUMENT_TEMPLATE_STATES.DRAFT,
  });

  const menuItems = editorMenuItems({
    t,
    pendingFile,
    zoneSource,
    preview,
    hasDraft,
  });

  const samplePrefix =
    testData.kind === TEST_DATA_KINDS.SAMPLE
      ? t("ZONES.WITH_SAMPLE", { sample: testData.label })
      : t("ZONES.WITH_PROJECT");

  const pointedIds = isNil(pointerZoneId) ? [] : [pointerZoneId];
  const openIds = isNil(panelZone) ? [] : [panelZone.id];
  const focusedIds = isNil(focusZoneId) ? [] : [focusZoneId];
  const activeIds =
    [groupHoverIds, pointedIds, openIds, focusedIds].find(
      (ids) => !isEmpty(ids),
    ) ?? [];

  const renderPanel = (zone) =>
    zone.id === panelZone?.id ? (
      <FieldPanel
        key={zone.id}
        zone={zone}
        mappings={panel.mappings}
        proposal={pendingProposalOf(zone, content.proposals)}
        payload={testData.payload}
        resultLabel={samplePrefix}
        canSave={isZoneSettled(zone, panel.mappings)}
        onChange={(mappings) =>
          setPanel((current) => ({ ...current, mappings }))
        }
        onClose={() => setPanel(null)}
        onConfirm={confirmPanel}
      />
    ) : null;

  const rowActions = {
    payload: testData.payload,
    proposals: content.proposals,
    searching: lucie.running,
    samplePrefix,
    statusOf,
    groups,
    mode: sections.mode,
    placeOf: sections.placeOf ?? NO_PLACES,
    pointerZoneId,
    titleOf,
    renderPanel,
    onToggle: toggleZone,
    onFill: fillZone,
    onOpen: openZone,
    onLeaveEmpty: leaveEmpty,
    onHover: hoverRow,
    onFocus: focusRow,
    onBlur: blurRow,
  };

  const side = () => {
    if (view === VIEWS.ALL || isEmpty(unsettled))
      return (
        <AllFields
          {...rowActions}
          zones={zones}
          mappings={content.anchorMappings}
          allSettled={isEmpty(unsettled)}
          unsettledCount={unsettled.length}
          openZoneId={panelZone?.id ?? null}
          focus={listFocus}
          onBack={() => setView(VIEWS.SETTLE)}
        />
      );
    return (
      <SettleList
        {...rowActions}
        zones={unsettled}
        allZones={zones}
        openZone={panelZone}
        mappings={content.anchorMappings}
        leaving={leaving}
        total={zones.length}
        onFillGroup={fillGroup}
        onLeaveGroup={leaveGroup}
        onGroupHover={setGroupHoverIds}
        onSeeAll={() => setView(VIEWS.ALL)}
      />
    );
  };

  return (
    <PayloadPathsProvider paths={paths}>
      <div className="dynamic-documents-editor">
        <EditorHeader
          name={content.name}
          saving={draft.saving}
          failing={draft.failing}
          onRetry={draft.retry}
          onBack={() =>
            navigate(SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.LIST.path)
          }
          lucie={{
            running: lucie.running,
            disabled: lucie.running,
            onAsk: () => lucie.ask(unsettled),
          }}
          menu={{
            items: menuItems,
            onClick: (key) => MENU_ACTIONS[key]?.(),
          }}
          publish={{
            state: publishState,
            ready,
            total,
            onPublish: () => setSheet(SHEETS.PUBLISH),
          }}
        >
          <UsageLine
            uses={content.uses}
            plans={plans}
            planNames={planNames}
            open={usesOpen}
            onOpenChange={setUsesOpen}
            onChange={(uses) => update((current) => ({ ...current, uses }))}
          />
        </EditorHeader>
        <div className="dynamic-documents-editor__body">
          <aside ref={sideRef} className="dynamic-documents-editor__side">
            {intro.shown && <EditorIntro onDismiss={intro.dismiss} />}
            {side()}
          </aside>
          <PreviewPane
            preview={preview}
            zoneSource={zoneSource}
            showsSource={
              !isNil(pendingFile) || get(zoneSource, "source.pending") === true
            }
            boxes={boxes}
            zoneCount={zones.length}
            zoneInfo={zoneInfo}
            activeIds={activeIds}
            selectedZoneId={panelZone?.id ?? null}
            focus={previewFocus}
            ghostOf={ghostOf}
            onZoneHover={setPointerZoneId}
            onZoneClick={openFromPreview}
            picker={
              <TestDataPicker
                selection={testData}
                realOpen={realOpen}
                onRealOpenChange={setRealOpen}
                onSelectSample={(key) => setTestData(sampleSelection(key))}
                onSelectReal={({ label, payload, paths: schemaPaths }) => {
                  setTestData({
                    kind: TEST_DATA_KINDS.REAL,
                    label,
                    payload,
                    paths: schemaPaths,
                  });
                  setTestedWithRealProject(true);
                }}
              />
            }
          />
        </div>
        <PublishSheet
          open={sheet === SHEETS.PUBLISH}
          content={content}
          template={template}
          pendingFile={pendingFile}
          planNames={planNames}
          testedWithRealProject={testedWithRealProject}
          publishing={publishing}
          onClose={() => setSheet(null)}
          onPublish={publish}
          onChooseProduct={chooseProduct}
          onTestReal={() => setRealOpen(true)}
        />
        <HistorySheet
          open={sheet === SHEETS.HISTORY}
          template={{ id }}
          content={content}
          onClose={() => setSheet(null)}
          onRestore={restore}
          onUndoProposal={(zone) =>
            update((current) => undoProposal(current, zone))
          }
        />
        <ReplacePdf
          open={modal === MODALS.REPLACE_PDF}
          content={content}
          onClose={() => setModal(null)}
          onApply={applyNewFile}
        />
        {modal === MODALS.RENAME && (
          <RenameModal
            name={content.name}
            onClose={() => setModal(null)}
            onRename={(name) => {
              update((current) => ({ ...current, name }));
              setModal(null);
            }}
          />
        )}
        <input
          ref={importRef}
          type="file"
          accept=".json,application/json"
          className="dynamic-documents__hidden-input"
          onChange={importConfig}
        />
      </div>
    </PayloadPathsProvider>
  );
};

export default Edit;
